export class PoolConnectionModel {
  type: string;
  locked: boolean;
  validCredentials: boolean;
  settings: PoolConnectionSettingsModel;
  credentials: PoolConnectionCredentialsModel;
}

export class PoolConnectionCredentialsModel {
  username: string;
  subdomain: string;
}

export type MatchProperty = 'FIRST_NAME' | 'LAST_NAME' | 'BIRTH_DATE' | 'ADDRESS' | 'EMAIL' | 'PHONE_NUMBER' | 'TAX_ID';

export class PoolConnectionSettingsModel {
  clientCreationAutoSyncOn: ['CLIENT' | 'ADVISOR'];
  clientUpdateAutoSyncOn: ['CLIENT' | 'ADVISOR'];
  clientDeletionAutoSyncOn: ['CLIENT' | 'ADVISOR'];
  autoCreateMandate: boolean;
  createVinlivtClientOnSync: boolean;
  recreateClientInPoolOnSync: boolean;
  createClientInPoolOnSync: boolean;
  matchProperties: MatchProperty[][];
}

// Mirrors account.PoolSynchronizationReport — persisted on the sync job, only set once COMPLETED.
export type PoolSyncFailureReason = 'DUPLICATED_EMAIL' | 'MISSING_NAME' | 'MINOR' | 'UNKNOWN_ERROR';

export interface PoolSyncReportFailedClient {
  userId: string | null;
  poolLink: string | null;
  name: string;
  reason: PoolSyncFailureReason;
  // Only set for DUPLICATED_EMAIL: the contested email and who already holds it
  email?: string | null;
  conflictsWith?: string | null;
}

export interface PoolSyncReportClientRef {
  userId: string | null;
  poolLink?: string | null;
  name: string;
}

export interface PoolSyncReportModel {
  direction: 'POOL_TO_VINLIVT' | 'VINLIVT_TO_POOL';
  clientCountPool: number;
  clientCountVinlivt: number;
  matchedClients: PoolSyncReportClientRef[] | null;
  newClients: PoolSyncReportClientRef[] | null;
  deletedClients: string[] | null;
  unlinkedClients: PoolSyncReportClientRef[] | null;
  updatedClients: PoolSyncReportClientRef[] | null;
  failedClients: PoolSyncReportFailedClient[] | null;
}

export class PoolConnectionStatusModel {
  // Job info
  jobId: string;
  direction: 'POOL_TO_VINLIVT' | 'VINLIVT_TO_POOL';
  poolType: 'NETFONDS' | 'BERATERWELT' | 'IPLANNER';
  // FULL = whole client base; SINGLE_CLIENT = one client (name in clientLabel)
  scope: 'FULL' | 'SINGLE_CLIENT';
  // Display name of the synced client. Only set when scope=SINGLE_CLIENT.
  clientLabel: string | null;
  // User id of the synced client. Only set when scope=SINGLE_CLIENT (null for older jobs).
  clientUserId: string | null;

  // Status
  status: 'RUNNING' | 'COMPLETED' | 'FAILED';
  // PREPARING sits between CONNECTING and SYNCING_CLIENTS: the backend is loading the full client
  // list from the pool (e.g. IPLANNER) and diffing it against Vinlivt. The client total is still
  // unknown here, so the FE shows an indeterminate "loading client data" state instead of "0 / 0".
  currentStage: 'CONNECTING' | 'PREPARING' | 'SYNCING_CLIENTS' | 'SYNCING_CONTRACTS';

  // Progress metrics
  clients: {
    total: number;
    processed: number;
    transferred: number;
    matched: number;
    conflicts: number;
  };

  // Timeline
  startedAt: string;
  completedAt: string | null;

  // Events
  recentEvents: Array<{
    ts: string;
    level: 'STAGE' | 'INFO' | 'OK' | 'MATCH' | 'CONFLICT';
    code: string;
    metadata: Record<string, unknown> | null;
  }>;

  // Live "currently processing" label for the SYNCING_CLIENTS stage. Backend pushes ~2x/s.
  // Null outside that stage and after a terminal state.
  currentClientLabel?: string | null;
  poolConnectionLocked: boolean;

  // Final report — only set once status is COMPLETED
  report?: PoolSyncReportModel | null;
}

// Mirrors b2bapi.ContractSyncJobSnapshotDto. Pushed via WebSocket under action `b2bapi/sync-progress`.
// FE matches it to a parent SyncJob by `parentJobId` and renders both as a single timeline.
export type ContractSyncOutcome =
  | 'CREATED'
  | 'UPDATED'
  | 'SKIPPED_EXPIRED'
  | 'SKIPPED_STATUS'
  | 'SKIPPED_NO_GROUP'
  | 'SKIPPED_PERSON_MISMATCH'
  | 'ERROR';

export class ContractSyncReportEntryModel {
  poolContractId: string;
  contractNumber: string;
  poolProviderName: string;
  outcome: ContractSyncOutcome;
  detail: string;
  providerPending: boolean;
  vinlivtContractUid: string;
}

export class ContractSyncReportClientModel {
  clientUserId: string;
  clientName: string;
  entries: Array<ContractSyncReportEntryModel>;
}

export class ContractSyncReportModel {
  pool: string;
  totals: { [key: string]: number };
  clients: Array<ContractSyncReportClientModel>;
}

export class ContractSyncProgressModel {
  jobId: string;
  parentJobId: string;
  advisorId: string;
  status: 'RUNNING' | 'COMPLETED' | 'FAILED';
  report?: ContractSyncReportModel;

  contracts: {
    total: number;
    processed: number;
    matched: number;
    transferred: number;
    conflicts: number;
  };

  startedAt: string;
  completedAt: string | null;

  recentEvents: Array<{
    ts: string;
    level: 'STAGE' | 'INFO' | 'OK' | 'MATCH' | 'CONFLICT';
    code: string;
    metadata: Record<string, unknown> | null;
  }>;
}
